import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaHandHoldingUsd, FaMoneyBillWave, FaCheckCircle, FaHistory } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import useColleges from '../../../../utils/useColleges';
import './AdvanceSalary.css';

const PAYMENT_TYPES = ['Cash', 'Bank Transfer', 'Cheque', 'Net Banking', 'UPI', 'Other'];

const EMPTY_ADVANCE = { date: '', amount: '', narration: '', paymentType: 'Cash', bank: '' };

const AdvanceSalary = () => {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();
  const navigate = useNavigate();

  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedDept, setSelectedDept]       = useState('');
  const [deptSearchInput, setDeptSearchInput] = useState('All Departments');
  const [showDeptDrop, setShowDeptDrop]       = useState(false);
  const [departments, setDepartments]         = useState([]);
  const [employees, setEmployees]             = useState([]);
  const [banks, setBanks]                     = useState([]);
  const [expandedRow, setExpandedRow]         = useState(null);
  const [advanceForm, setAdvanceForm]         = useState(EMPTY_ADVANCE);
  const [saving, setSaving]                   = useState(false);

  const deptDropRef = useRef(null);

  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);
  useEffect(() => {
    if (isAdmin && colleges.length === 1) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);

  // Load banks
  useEffect(() => {
    api.get('/banks', { params: { limit: 1000 } })
      .then(r => setBanks(r.data.data || []))
      .catch(() => setBanks([]));
  }, []);

  useEffect(() => {
    if (!selectedCollege) { setDepartments([]); setEmployees([]); return; }
    api.get('/departments', { params: { college: selectedCollege } })
      .then(r => setDepartments(r.data.data || [])).catch(() => {});
    api.get('/employees', { params: { limit: 500 } })
      .then(r => setEmployees((r.data.data || []).filter(e =>
        String(e.college?._id || e.college) === String(selectedCollege) && e.isActive !== false
      ))).catch(() => {});
  }, [selectedCollege]);

  useEffect(() => {
    const handler = (e) => {
      if (deptDropRef.current && !deptDropRef.current.contains(e.target)) {
        setShowDeptDrop(false);
        setDeptSearchInput(
          selectedDept ? departments.find(d => d._id === selectedDept)?.name || 'All Departments' : 'All Departments'
        );
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedDept, departments]);

  const filteredDepts = useMemo(() => {
    const q = deptSearchInput.toLowerCase();
    if (q === 'all departments' || q === '') return departments;
    return departments.filter(d => d.name.toLowerCase().includes(q));
  }, [deptSearchInput, departments]);

  const displayedEmployees = useMemo(() => {
    if (!selectedDept) return employees;
    return employees.filter(e => String(e.department?._id || e.department) === selectedDept);
  }, [employees, selectedDept]);

  const handleToggleAdvanceForm = (empId) => {
    if (expandedRow === empId) { setExpandedRow(null); return; }
    setExpandedRow(empId);
    setAdvanceForm({ ...EMPTY_ADVANCE, date: new Date().toISOString().split('T')[0] });
  };

  const validateAdvance = () => {
    if (!advanceForm.date)          { Swal.fire('Required', 'Please select date of advance.', 'warning'); return false; }
    if (!advanceForm.amount || parseFloat(advanceForm.amount) <= 0) { Swal.fire('Required', 'Please enter a valid amount.', 'warning'); return false; }
    if (!advanceForm.narration.trim()) { Swal.fire('Required', 'Please enter narration.', 'warning'); return false; }
    if (!advanceForm.paymentType)   { Swal.fire('Required', 'Please select payment type.', 'warning'); return false; }
    if (advanceForm.paymentType !== 'Cash' && !advanceForm.bank) {
      Swal.fire('Bank Required', 'Please select a debited bank for non-cash payment.', 'warning'); return false;
    }
    return true;
  };

  const handlePayAdvance = async (emp) => {
    if (!validateAdvance()) return;
    setSaving(true);
    try {
      const payload = {
        amount:      parseFloat(advanceForm.amount),
        date:        advanceForm.date,
        narration:   advanceForm.narration,
        paymentType: advanceForm.paymentType,
        bank:        advanceForm.paymentType !== 'Cash' ? advanceForm.bank : null,
      };
      const { data } = await api.patch(`/employees/${emp._id}/advance`, payload);
      setEmployees(prev => prev.map(e => e._id === emp._id ? { ...e, advanceBalance: data.data.advanceBalance } : e));
      Swal.fire('Success!', `₹${payload.amount} advance given to ${emp.name}.`, 'success');
      setExpandedRow(null);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to process advance.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="advance-payment-container">
      <h2 className="advance-payment-title">Employee Advance Salary</h2>

      {/* Filters */}
      <div className="advance-payment-filters-wrapper">
        {isAdmin && (
          <div className="advance-payment-filter-group">
            <label className="advance-payment-filter-label">College</label>
            <select className="advance-payment-select-input" value={selectedCollege}
              onChange={e => { setSelectedCollege(e.target.value); setSelectedDept(''); setDeptSearchInput('All Departments'); setExpandedRow(null); }}>
              <option value="">-- Select College --</option>
              {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div className="advance-payment-filter-group" ref={deptDropRef}>
          <label className="advance-payment-filter-label">Department</label>
          <div className="advance-payment-searchable-dropdown">
            <FaSearch className="advance-payment-search-icon" />
            <input type="text" className="advance-payment-dropdown-input"
              value={deptSearchInput}
              onChange={e => { setDeptSearchInput(e.target.value); setShowDeptDrop(true); }}
              onClick={() => { setDeptSearchInput(''); setShowDeptDrop(true); }}
              placeholder="Search department..." />
            <FaChevronDown className="advance-payment-chevron-icon" />
            {showDeptDrop && (
              <div className="advance-payment-dropdown-list">
                <ul>
                  <li onClick={() => { setSelectedDept(''); setDeptSearchInput('All Departments'); setShowDeptDrop(false); }}>All Departments</li>
                  {filteredDepts.map(d => (
                    <li key={d._id} onClick={() => { setSelectedDept(d._id); setDeptSearchInput(d.name); setShowDeptDrop(false); }}>{d.name}</li>
                  ))}
                  {filteredDepts.length === 0 && <li className="advance-payment-dropdown-empty">No departments found</li>}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="advance-payment-table-wrapper">
        <table className="advance-payment-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="advance-payment-align-left">Employee Name</th>
              <th className="advance-payment-col-dept">Department</th>
              <th className="advance-payment-col-salary">Monthly Salary</th>
              <th className="advance-payment-col-advance">Already Advance</th>
              <th className="advance-payment-col-log">Log</th>
              <th>Give Advance</th>
            </tr>
          </thead>
          <tbody>
            {displayedEmployees.length === 0 ? (
              <tr><td colSpan="7" className="advance-payment-empty-msg">
                {selectedCollege ? 'No employees found.' : 'Please select a college.'}
              </td></tr>
            ) : displayedEmployees.map((emp, i) => {
              const advanceBal = emp.advanceBalance || 0;
              const isExpanded = expandedRow === emp._id;
              return (
                <React.Fragment key={emp._id}>
                  <tr className={`advance-payment-main-row ${isExpanded ? 'advance-payment-active-row' : ''}`}>
                    <td>{i + 1}</td>
                    <td className="advance-payment-align-left advance-payment-emp-name">{emp.name}</td>
                    <td className="advance-payment-col-dept">
                      <span className="advance-payment-badge-dept">{emp.department?.name || '—'}</span>
                    </td>
                    <td className="advance-payment-col-salary">
                      <span className="advance-payment-salary-text">₹ {(emp.monthlySalary || emp.salary || 0).toLocaleString()}</span>
                    </td>
                    <td className="advance-payment-col-advance">
                      {advanceBal > 0
                        ? <span className="advance-payment-advance-text">₹ {advanceBal.toLocaleString()}</span>
                        : <span className="advance-payment-advance-zero">₹ 0</span>}
                    </td>
                    <td className="advance-payment-col-log">
                      <button className="advance-payment-btn-log" title="View Log"
                        onClick={() => navigate(`${user?.role === 'admin' ? '/admin' : '/collegeadmin'}/salary/advance/log/${emp._id}`)}>
                        <FaHistory /> Log
                      </button>
                    </td>
                    <td>
                      <button className={`advance-payment-btn-toggle ${isExpanded ? 'active' : ''}`}
                        onClick={() => handleToggleAdvanceForm(emp._id)}>
                        <FaHandHoldingUsd /> {isExpanded ? 'Cancel' : 'Give Advance'}
                      </button>
                    </td>
                  </tr>

                  {/* Give Advance Form */}
                  {isExpanded && (
                    <tr className="advance-payment-expanded-row">
                      <td colSpan="7">
                        <div className="advance-payment-form-container">
                          <div className="advance-payment-form-header">
                            <FaMoneyBillWave className="advance-payment-form-icon" />
                            <span>Give Advance to <strong>{emp.name}</strong></span>
                          </div>

                          <div className="advance-payment-form-grid">

                            {/* Date */}
                            <div className="advance-payment-form-group">
                              <label>Date of Advance <span className="advance-req">*</span></label>
                              <input type="date" value={advanceForm.date}
                                onChange={e => setAdvanceForm(f => ({ ...f, date: e.target.value }))} />
                            </div>

                            {/* Amount */}
                            <div className="advance-payment-form-group">
                              <label>Amount (₹) <span className="advance-req">*</span></label>
                              <input type="number" placeholder="e.g. 5000" min="1"
                                value={advanceForm.amount}
                                onChange={e => setAdvanceForm(f => ({ ...f, amount: e.target.value }))} />
                            </div>

                            {/* Payment Type */}
                            <div className="advance-payment-form-group">
                              <label>Payment Type <span className="advance-req">*</span></label>
                              <select value={advanceForm.paymentType}
                                onChange={e => setAdvanceForm(f => ({ ...f, paymentType: e.target.value, bank: '' }))}>
                                {PAYMENT_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
                              </select>
                            </div>

                            {/* Debited Bank — hidden for Cash */}
                            {advanceForm.paymentType !== 'Cash' && (
                              <div className="advance-payment-form-group">
                                <label>Debited Bank <span className="advance-req">*</span></label>
                                <select 
                                  value={advanceForm.bank || ''} 
                                  onChange={e => setAdvanceForm(f => ({ ...f, bank: e.target.value }))}
                                  style={!advanceForm.bank ? {borderColor: '#ef4444'} : {}}
                                >
                                  <option value="">-- Select Bank --</option>
                                  {banks.filter(b => !selectedCollege || b.colleges?.some(c => String(c._id || c) === String(selectedCollege))).map(b => (
                                    <option key={b._id} value={b._id}>
                                      {b.name} - {b.accountNumber}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {/* Narration — full width */}
                            <div className="advance-payment-form-group advance-full-width">
                              <label>Narration <span className="advance-req">*</span></label>
                              <input type="text" placeholder="e.g. Medical emergency advance"
                                value={advanceForm.narration}
                                onChange={e => setAdvanceForm(f => ({ ...f, narration: e.target.value }))} />
                            </div>

                          </div>

                          <div className="advance-payment-form-buttons">
                            <button className="advance-payment-btn-cancel" onClick={() => setExpandedRow(null)} disabled={saving}>Cancel</button>
                            <button className="advance-payment-btn-pay" onClick={() => handlePayAdvance(emp)} disabled={saving}>
                              {saving ? 'Processing...' : <><FaCheckCircle /> Confirm Advance</>}
                            </button>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdvanceSalary;
